import {
  Alignment,
  Button,
  Classes,
  Colors,
  HTMLSelect,
  InputGroup,
  Menu,
  MenuItem,
  Navbar,
  Popover,
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { showError } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { ErrorDetails, LanguageApi, RequestApi, UsersApi } from "../api";
import assets from "../assets";
import {
  BreadcrumbContext,
  useDetailHistoryContext,
  useGlobalData,
} from "../contexts";
import { useAuth } from "../contexts";
import { ERoutes } from "../ERoutes";
import { useApiService, useTl } from "../hooks";
import { useReferential } from "../hooks/useReferential";
import { ETLCodes } from "../locales";
import { getRouteStr } from "../utils/RequestRouteUtil";
import { AppBreadcrumb } from "./AppBreadcrumb";
import { SearchButton } from "./applicationButtons";

interface IHeaderProps {
  showMenuIcon: boolean;
  toggleLeftMenu?: () => void;
  isDark?: boolean;
  onDarkSwitchChange?: (e: boolean) => void;
}

const NavBarStyled = styled(Navbar)`
  background-image: linear-gradient(
      to right,
      #0e5a8a,
      #0e5a8a 25%,
      rgba(255, 255, 255, 0.5),
      white 65%,
      #0e5a8a 80%
    ),
    url(${assets.images.dogBg2}) !important;
  background-position: 0% 35%;
  height: 75px !important;
  & > .${Classes.NAVBAR_GROUP} {
    height: 75px;
  }
  /* background-color: ${Colors.BLUE1} !important; */
`;
const LogoContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;
const HeaderText = styled.div`
  font-size: 1.2rem;
  font-weight: bold;
  font-family: "Playfair Display", serif;
  padding-left: 2rem;
`;

const SrshImage = styled.img`
  height: 70px;
`;

const RightContainer = styled.div`
  display: flex;
  flex-direction: row;
  & > * + * {
    margin-left: 0.5rem;
  }
`;

export const Header: React.FC<IHeaderProps> = (props) => {
  const history = useHistory();
  const { logout, user } = useAuth();
  const { t, tUnsafe } = useTl();
  const globalData = useGlobalData();
  const [languages, languagesLoading] = useReferential((c) => c.getLanguages());
  const languageApi = useApiService(LanguageApi);
  const requestApi = useApiService(RequestApi);
  const userApi = useApiService(UsersApi);
  const { breadcrumbs } = React.useContext(BreadcrumbContext);
  const { actionHistory } = useDetailHistoryContext();
  const { userPermissions } = useAuth();
  const [requestSearch, setrequestSearch] = React.useState<string>();
  const [searchLoading, setsearchLoading] = React.useState<boolean>();

  const changeLanguage = React.useCallback(
    async (c) => {
      const language = await languageApi.getByLanguageId(+c.target.value);
      if (user)
        await userApi.changeUserLanguage(user?.userId, language.languageId);
      globalData.setLanguage(language);
    },
    [globalData, languageApi, user, userApi]
  );
  const searchRequest = React.useCallback(async () => {
    setsearchLoading(true);
    try {
      const subRequestInfo = await requestApi.getByRequestNumberOrInvoice(
        requestSearch
      );
      const route = getRouteStr(
        subRequestInfo.requestType,
        subRequestInfo.subRequestId,
        "view"
      );
      history.push(route);
    } catch (error) {
      const details: ErrorDetails = JSON.parse(error.response);
      if (details?.errorCode) {
        showError(tUnsafe(`BackendError_${details.errorCode}`));
      } else {
        showError(t(ETLCodes.Error));
      }
    }
    setsearchLoading(false);
  }, [history, requestApi, requestSearch, t, tUnsafe]);

  return (
    <div className={Classes.DARK}>
      <NavBarStyled fixedToTop>
        <Navbar.Group align={Alignment.LEFT}>
          {props.toggleLeftMenu && props.showMenuIcon && (
            <>
              <Button
                icon={IconNames.MENU}
                minimal={true}
                onClick={props.toggleLeftMenu}
              />
              <Navbar.Divider />
            </>
          )}
          <LogoContainer style={{ minWidth: 224, textAlign: "center" }}>
            <SrshImage src={assets.images.srshLogo} alt="SRSH" />
            <HeaderText>SRSH-KMSH</HeaderText>
          </LogoContainer>
          <Navbar.Divider />
          <Navbar.Heading
            style={{
              cursor: "pointer",
              fontSize: "1.3rem",
            }}
            onClick={() => {
              history.push(ERoutes.home);
            }}
          />
        </Navbar.Group>
        <Navbar.Group align={Alignment.LEFT}>
          <Navbar.Heading>
            <div
              style={{
                marginLeft: 0,
                mixBlendMode: "screen",
              }}
            >
              <AppBreadcrumb items={breadcrumbs} />
            </div>
          </Navbar.Heading>
        </Navbar.Group>
        <Navbar.Group align={Alignment.RIGHT}>
          <RightContainer>
            {user &&
              userPermissions &&
              userPermissions.some(
                (c) =>
                  c.tab?.toUpperCase() === "SEARCHREQUEST" &&
                  c.module?.toUpperCase() === "HEADER" &&
                  c.permission === "E"
              ) && (
                <InputGroup
                  style={{ backgroundColor: "white", color: "black" }}
                  value={requestSearch}
                  onChange={(e) => setrequestSearch(e.target.value)}
                  type="search"
                  rightElement={
                    <SearchButton
                      text={t(ETLCodes.Search)}
                      onClick={searchRequest}
                      loading={searchLoading}
                    />
                  }
                  placeholder={t(ETLCodes.Search)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      searchRequest();
                    }
                  }}
                />
              )}

            {actionHistory.length > 0 && user && (
              <Popover
                content={
                  <Menu>
                    {actionHistory.map((d, i) => {
                      return (
                        <MenuItem
                          key={i}
                          text={t(d.tlCode, { name: d.name })}
                          onClick={() => history.push(d.route)}
                        />
                      );
                    })}
                  </Menu>
                }
              >
                <Button
                  icon={IconNames.HISTORY}
                  text={t(ETLCodes.Historique)}
                  minimal={true}
                />
              </Popover>
            )}
            {!languagesLoading && (
              <HTMLSelect
                options={languages}
                value={globalData?.currentLanguage?.languageId ?? 1}
                onChange={changeLanguage}
              />
            )}
            {user && (
              <Popover
                content={
                  <Menu>
                    <MenuItem
                      text={t(ETLCodes.Logout)}
                      icon={IconNames.LOG_OUT}
                      onClick={() => logout("NORMAL")}
                    />
                  </Menu>
                }
              >
                <Button
                  icon="user"
                  text={`${user?.lastName} ${user?.firstName}`}
                  minimal={true}
                />
              </Popover>
            )}
          </RightContainer>
        </Navbar.Group>
      </NavBarStyled>
    </div>
  );
};
